import React, { useState, useEffect } from 'react';
import { Users, DollarSign, Clock, CheckCircle, TrendingUp, Calendar, Plus, User } from 'lucide-react';
import PaymentForm from './PaymentForm';

const Payroll = ({ apiKey }) => {
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState(null);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPayrollData();
  }, []);

  const fetchPayrollData = async () => {
    try {
      setLoading(true);
      
      // Fetch summary
      const summaryRes = await fetch(`${apiUrl}/api/payroll/summary/`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      
      if (summaryRes.status === 401) {
        console.error('❌ Clé API invalide - reconnexion requise');
        alert('Clé API invalide. Veuillez vous déconnecter et reconnecter.');
        setLoading(false);
        return;
      }
      
      if (!summaryRes.ok) {
        throw new Error(`HTTP error! status: ${summaryRes.status}`);
      }
      
      const summaryData = await summaryRes.json();
      setSummary(summaryData);

      // Fetch all payments
      const paymentsRes = await fetch(`${apiUrl}/api/payroll/payments/?limit=100`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (paymentsRes.ok) {
        const paymentsData = await paymentsRes.json();
        setPayments(paymentsData);
      }

      // Fetch workers summary
      const workersRes = await fetch(`${apiUrl}/api/payroll/workers/`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (workersRes.ok) {
        const workersData = await workersRes.json();
        setWorkers(workersData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching payroll data:', error);
      setLoading(false);
    }
  };

  const handlePaymentSave = () => {
    setShowPaymentForm(false);
    setSelectedPayment(null);
    fetchPayrollData();
  };

  const updatePaymentStatus = async (paymentId, status) => {
    try {
      await fetch(`${apiUrl}/api/payroll/payments/${paymentId}/status?status=${status}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      fetchPayrollData();
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const deletePayment = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement ?')) return;
    
    try {
      await fetch(`${apiUrl}/api/payroll/payments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      fetchPayrollData();
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  const viewWorkerDetails = (worker) => {
    setSelectedWorker(worker);
    setActiveTab('worker-details');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement des données de paie...</div>
      </div>
    );
  }

  const pendingPayments = payments.filter(p => p.payment_status === 'pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Paiement des Travailleurs</h2>
          <p className="text-gray-600">Gestion des salaires photographes et vidéastes</p>
        </div>
        <button
          onClick={() => setShowPaymentForm(true)}
          className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nouveau Paiement
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'dashboard'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Tableau de Bord
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'pending'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            En Attente ({pendingPayments.length})
          </button>
          <button
            onClick={() => setActiveTab('workers')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'workers'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Travailleurs ({workers.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-4 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'history'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Historique
          </button>
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && summary && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Total Workers */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <Users className="h-8 w-8 opacity-80" />
                <span className="text-blue-100 text-sm">Travailleurs</span>
              </div>
              <div className="text-3xl font-bold mb-1">{summary.total_workers}</div>
              <div className="text-blue-100 text-sm">Total actifs</div>
            </div>

            {/* Pending Payments */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <Clock className="h-8 w-8 opacity-80" />
                <span className="text-orange-100 text-sm">En attente</span>
              </div>
              <div className="text-3xl font-bold mb-1">${summary.total_pending.toLocaleString()}</div>
              <div className="text-orange-100 text-sm">{summary.pending_payments_count} paiements</div>
            </div>

            {/* Completed Payments */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <CheckCircle className="h-8 w-8 opacity-80" />
                <span className="text-green-100 text-sm">Payé</span>
              </div>
              <div className="text-3xl font-bold mb-1">${summary.total_completed.toLocaleString()}</div>
              <div className="text-green-100 text-sm">{summary.completed_payments_count} paiements</div>
            </div>

            {/* Total Amount */}
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <DollarSign className="h-8 w-8 opacity-80" />
                <span className="text-purple-100 text-sm">Total</span>
              </div>
              <div className="text-3xl font-bold mb-1">${summary.total_amount.toLocaleString()}</div>
              <div className="text-purple-100 text-sm">Tous paiements</div>
            </div>
          </div>

          {/* By Role */}
          {Object.keys(summary.by_role).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-amber-600" />
                Paiements par Rôle
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(summary.by_role).map(([role, data]) => (
                  <div key={role} className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">{role}</div>
                    <div className="text-2xl font-bold text-gray-900">${data.amount.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">{data.count} paiement(s)</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monthly Trend */}
          {summary.by_month && summary.by_month.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-600" />
                Tendance Mensuelle
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mois</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">En attente</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Payé</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {summary.by_month.map((month) => (
                      <tr key={month.month}>
                        <td className="px-4 py-3 text-sm text-gray-900">{month.month}</td>
                        <td className="px-4 py-3 text-sm text-right text-orange-600 font-medium">
                          ${month.pending.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">
                          ${month.completed.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900 font-semibold">
                          ${month.total.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending Payments Tab */}
      {activeTab === 'pending' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              Paiements en Attente
            </h3>
            {pendingPayments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travailleur</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pendingPayments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{payment.worker_name}</div>
                          <div className="text-xs text-gray-500">{payment.worker_phone}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{payment.worker_role}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {payment.work_period_start && payment.work_period_end
                            ? `${payment.work_period_start} → ${payment.work_period_end}`
                            : payment.payment_date
                          }
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                          ${payment.total_amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right space-x-2">
                          <button
                            onClick={() => updatePaymentStatus(payment.id, 'completed')}
                            className="text-green-600 hover:text-green-800 font-medium"
                          >
                            Marquer Payé
                          </button>
                          <button
                            onClick={() => {
                              setSelectedPayment(payment);
                              setShowPaymentForm(true);
                            }}
                            className="text-amber-600 hover:text-amber-800"
                          >
                            Modifier
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Aucun paiement en attente</p>
            )}
          </div>
        </div>
      )}

      {/* Workers Tab */}
      {activeTab === 'workers' && (
        <div className="space-y-6">
          {workers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workers.map((worker) => (
                <div
                  key={worker.worker_name}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => viewWorkerDetails(worker)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">{worker.worker_name}</h4>
                      <div className="text-sm text-gray-600">{worker.worker_role}</div>
                      <div className="text-xs text-gray-500">{worker.worker_phone}</div>
                    </div>
                    <User className="h-8 w-8 text-gray-400" />
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total payé</span>
                      <span className="font-semibold text-green-600">${worker.completed_amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">En attente</span>
                      <span className="font-semibold text-orange-600">${worker.pending_amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total général</span>
                      <span className="font-bold text-gray-900">${worker.total_amount.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-200 flex justify-between text-xs text-gray-500">
                    <span>{worker.total_payments} paiement(s)</span>
                    <span>{worker.events_count} événement(s)</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Aucun travailleur enregistré</p>
              <button
                onClick={() => setShowPaymentForm(true)}
                className="text-amber-600 hover:text-amber-800 font-medium"
              >
                Créer le premier paiement
              </button>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique Complet</h3>
            {payments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travailleur</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Méthode</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{payment.payment_date}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{payment.worker_name}</div>
                          <div className="text-xs text-gray-500">{payment.worker_phone}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{payment.worker_role}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{payment.payment_method}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            payment.payment_status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : payment.payment_status === 'pending'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {payment.payment_status === 'completed' ? 'Payé' : 
                             payment.payment_status === 'pending' ? 'En attente' : 'Annulé'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                          ${payment.total_amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right space-x-2">
                          <button
                            onClick={() => {
                              setSelectedPayment(payment);
                              setShowPaymentForm(true);
                            }}
                            className="text-amber-600 hover:text-amber-800"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => deletePayment(payment.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Aucun paiement enregistré</p>
            )}
          </div>
        </div>
      )}

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <PaymentForm
          payment={selectedPayment}
          apiKey={apiKey}
          onClose={() => {
            setShowPaymentForm(false);
            setSelectedPayment(null);
          }}
          onSave={handlePaymentSave}
        />
      )}
    </div>
  );
};

export default Payroll;
